import * as THREE from 'three'
import Experience from '../Experience.js'
import vertexShader   from '../../shaders/flame/vertex.glsl'
import fragmentShader from '../../shaders/flame/fragment.glsl'

/**
 * Flame1
 * Particle-based candle flame using custom ShaderMaterial.
 * ~300 particles drift upward from the wick position with
 * turbulent XZ displacement driven by uTime.
 *
 * Positioned at the wick tip: (0, ~1.2, 0) after candle scale applied.
 * uFlicker uniform is updated each frame from Environment.flameLight.intensity.
 */
export default class Flame {
    constructor(position = new THREE.Vector3(0, 0, 0)) {
        this.experience    = new Experience()
        this.scene         = this.experience.scene
        this.time          = this.experience.time
        this.debug         = this.experience.debug
        this.initPosition  = position

        this.count = 800

        this.setGeometry()
        this.setMaterial()
        this.setPoints()
        this.setDebug()
    }

    setGeometry() {
        this.geometry = new THREE.BufferGeometry()

        const positions = new Float32Array(this.count * 3)
        const aSize     = new Float32Array(this.count)
        const aOffset   = new Float32Array(this.count)
        const aRandom   = new Float32Array(this.count * 3)

        for (let i = 0; i < this.count; i++) {
            const i3 = i * 3

            // Start all particles at wick base; shader drives upward drift
            positions[i3]     = (Math.random() - 0.5) * 0.02
            positions[i3 + 1] = 0
            positions[i3 + 2] = (Math.random() - 0.5) * 0.02

            aSize[i]       = 2 + Math.random() * 0.5
            aOffset[i]     = Math.random()          // lifecycle phase
            aRandom[i3]     = (Math.random() - 0.5)
            aRandom[i3 + 1] = Math.random()
            aRandom[i3 + 2] = (Math.random() - 0.5)
        }

        this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
        this.geometry.setAttribute('aSize',    new THREE.BufferAttribute(aSize,    1))
        this.geometry.setAttribute('aOffset',  new THREE.BufferAttribute(aOffset,  1))
        this.geometry.setAttribute('aRandom',  new THREE.BufferAttribute(aRandom,  3))
    }

    setMaterial() {
        this.material = new THREE.ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms: {
                uTime:    { value: 0 },
                uFlicker: { value: 1 },
                uFadeOut: { value: 1 },
                uSeed:    { value: Math.random() * 100.0 }, // ТЕПЕР ВІН ТУТ
            },
            transparent:    true,
            depthWrite:     false,
            blending:       THREE.AdditiveBlending,
            vertexColors:   false,
        })
    }

    setPoints() {
        this.points = new THREE.Points(this.geometry, this.material)
        // Wick tip: scale 0.02 × wick Y ≈ 11 = 0.22, model.y = -1 → net ≈ -0.78
        // Fine-tune via /#debug → Flame → Y pos
        this.points.position.copy(this.initPosition)
        this.scene.add(this.points)
    }

    setDebug() {
        if (!this.debug.active) return
        const folder = this.debug.ui.addFolder('Flame')
        folder.add(this.points.position, 'y').min(-1).max(3).step(0.01).name('Y pos')
        folder.add(this.material.uniforms.uTime, 'value').min(0).max(10).step(0.01).name('Time (override)')
    }

    update() {
        this.material.uniforms.uTime.value    = this.time.elapsed * 0.001
        // Flicker driven by Environment.flameLight — World.js passes it in
        // (see World.update)
    }

    setFlicker(value) {
        this.material.uniforms.uFlicker.value = value
    }

    setFade(value) {
        this.material.uniforms.uFadeOut.value = value
    }
}